import React, { useEffect, useCallback } from "react";
import styled from "styled-components";
import { Row, Col, Card, Form, Button, Stack } from "react-bootstrap";
import Table from "./Table";
import { useState } from "react";
import TableTitle from "../../organisms/TableTitle";
import { AgendamientoListado } from "../../../services/agendamientosGuarderiaServicios";
import { Pagina } from "../../../services/Pagina";
import PaginationPrecalculated from "../../organisms/PaginationPrecalculated";
import SuccessButton from "../../organisms/SuccessButton";

interface TableSectionProps {
  paginaAgendamientos?: Pagina<AgendamientoListado>;
  isLoading: boolean;
  actualizarFiltroQuery: (x: string) => void;
}

const construirQueryParamsListarTodos = (
  numeroPagina?: number,
  maxElementos?: number,
  nombrePropietario?: string,
  nombreMascota?: string,
  fechaInicio?: string,
  fechaFin?: string
): string => {
  let params: Map<string, string> = new Map();

  if (numeroPagina) {
    params.set("numeroPagina", numeroPagina.toString());
  }
  if (maxElementos) {
    params.set("maxElementos", maxElementos.toString());
  }
  if (nombrePropietario) {
    params.set("nombrePropietario", nombrePropietario);
  }
  if (nombreMascota) {
    params.set("nombreMascota", nombreMascota);
  }
  if (fechaInicio) {
    params.set("fechaInicio", fechaInicio);
  }
  if (fechaFin) {
    params.set("fechaFin", fechaFin);
  }
  if (params.size === 0) {
    return "";
  }

  const filterValues: string[] = Array.from(params)
    .map(entry => entry[0] + "=" + entry[1]);

  const joinedFilters = filterValues.join("&");

  return "?" + joinedFilters;
};

function TableSection({
  paginaAgendamientos,
  isLoading,
  actualizarFiltroQuery
}: TableSectionProps) {
  const [nombreMascotaFil, setNombreMascotaFil] = useState<string>("");
  const [nombrePropietarioFil, setNombrePropietarioFil] = useState<string>("");
  const [fechaInicioFil, setFechaInicioFil] = useState<string>("");
  const [fechaFinFil, setFechaFinFil] = useState<string>("");
  const [numeroPaginaFil, setNumeroPaginaFil] = useState<number>(1);
  const [maxElementosFil, setMaxElementosFil] = useState<number>(10);

  const dispararActualizacionQuery = useCallback(() => {
    actualizarFiltroQuery(construirQueryParamsListarTodos(
      numeroPaginaFil - 1,
      maxElementosFil,
      nombrePropietarioFil,
      nombreMascotaFil,
      fechaInicioFil,
      fechaFinFil
    ));
  }, [actualizarFiltroQuery, numeroPaginaFil, maxElementosFil, nombrePropietarioFil, nombreMascotaFil, fechaInicioFil, fechaFinFil]);

  const onBuscar = () => {
    dispararActualizacionQuery();
  }

  const paginaMostrar = paginaAgendamientos?.numero ? paginaAgendamientos.numero + 1 : 1;

  const incrementarPagina = (siguientePagina: number) => {
    setNumeroPaginaFil(siguientePagina);
  }

  const reiniciarFiltros = () => {
    setNombreMascotaFil("");
    setNombrePropietarioFil("");
    setFechaInicioFil("");
    setFechaFinFil("");
  }

  useEffect(() => {
    dispararActualizacionQuery();
  }, [maxElementosFil, numeroPaginaFil, dispararActualizacionQuery]);

  return (
    <Row className="justify-content-center">
      <Col className="col-10">
        <StyledCard>
          <StyledCardHeader>
            <TableTitle>Listado de Agendamientos</TableTitle>
          </StyledCardHeader>
          <StyledCardBody>
            <ResponsiveTableSection>
              <TableWrapper>
                <Stack gap={4}>
                  <TableLengthSection>
                    <Form>
                      <StyledFormGroup as={Row} className="mb-2">
                        <Form.Label column sm="auto">
                          Mascota:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormControl
                            type="text"
                            value={nombreMascotaFil}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setNombreMascotaFil(e.target.value)}
                          />
                        </Col>
                        <Form.Label column sm="auto">
                          Propietario:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormControl
                            type="text"
                            value={nombrePropietarioFil}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setNombrePropietarioFil(e.target.value)}
                          />
                        </Col>
                      </StyledFormGroup>
                      <StyledFormGroup as={Row} className="mb-2">
                        <Form.Label column sm="auto">
                          Fecha Inicio:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormControl
                            type="date"
                            value={fechaInicioFil}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setFechaInicioFil(e.target.value)}
                          />
                        </Col>
                        <Form.Label column sm="auto">
                          Fecha Fin:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormControl
                            type="date"
                            value={fechaFinFil}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setFechaFinFil(e.target.value)}
                          />
                        </Col>
                      </StyledFormGroup>
                      <StyledFormGroup className="d-flex gap-2">
                        <Form.Label column sm="auto">
                          Items:
                        </Form.Label>
                        <Col sm="auto">
                          <StyledFormSelect
                            value={maxElementosFil}
                            onChange={(
                              e: React.ChangeEvent<HTMLSelectElement>
                            ) => setMaxElementosFil(parseInt(e.target.value))}
                          >
                            {[5, 10, 15].map((pageSize, index) => (
                              <option key={index} value={pageSize}>
                                {pageSize}
                              </option>
                            ))}
                          </StyledFormSelect>
                        </Col>
                        <Col sm="auto">
                          <SuccessButton
                            onClick={onBuscar}
                          >
                            Buscar
                          </SuccessButton>
                        </Col>
                        <Col sm="auto">
                          <Button
                            variant="primary"
                            onClick={reiniciarFiltros}
                          >
                            Limpiar filtros
                          </Button>
                        </Col>
                      </StyledFormGroup>
                    </Form>
                  </TableLengthSection>
                  <Table isLoading={isLoading} agendamientos={paginaAgendamientos?.contenido || []} />
                  <div>
                    <PaginationPrecalculated
                      setActivePageNumber={incrementarPagina}
                      activePageNumber={paginaMostrar}
                      rows={paginaAgendamientos?.contenido || []}
                      hasNext={paginaAgendamientos?.tieneSiguiente || false}
                    />
                  </div>
                </Stack>
              </TableWrapper>
            </ResponsiveTableSection>
          </StyledCardBody>
        </StyledCard>
      </Col>
    </Row>
  );
}

const StyledCard = styled(Card)`
  margin-bottom: 1.875rem;
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  padding: 1.25rem 1.25rem 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 0px;
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;
const ResponsiveTableSection = styled("div")`
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TableWrapper = styled("div")`
  position: relative;
  clear: both;
  zoom: 1;
`;

const TableLengthSection = styled("div")`
  float: left;
`;

const StyledFormGroup = styled(Form.Group)`
  justify-content: start;
`;
const StyledFormControl = styled(Form.Control)`
  &:focus {
    box-shadow: none;
  }
`;

const StyledFormSelect = styled(Form.Select)`
  &:focus {
    box-shadow: none;
  }
`;

export default TableSection;
